import type { SipStartRecommendation } from '../../../../api/sipPlan.types'
import { SipCategoryBucketKey } from '../../../../api/types'
import {
  PlansFundCard,
  type PlanFundCardCta
} from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansFundCard'
import { useOpenSipPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenSipPlanRecommendationBottomSheet'
import { usePlanSipStartAction } from '../../../../../execution/sip/start/usePlanSipStartAction'
import { formatTargetSip } from '../../../../../display-values/sipPlanAmount'
import { useSipPlansContext } from '../../../sipPlansContext'
import { SipFundCardTopRightLabel } from './sipYourPlanFundCardShared'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type StartSipFundCardProps = {
  recommendation: SipStartRecommendation
  showBottomDivider?: boolean
}

export function StartSipFundCard({ recommendation, showBottomDivider = true }: StartSipFundCardProps) {
  const { plansOverview } = useSipPlansContext()
  const { openSipPlanRecommendationBottomSheet } = useOpenSipPlanRecommendationBottomSheet()
  const { onStartSip } = usePlanSipStartAction()
  const totalIdealSipAmount = plansOverview?.totalIdealSipAmount ?? 0
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.SipYourPlan
  const fundRole = PlansFundRole.StartSip
  const formattedTargetSip = formatTargetSip({
    rawAmount: recommendation?.recommendedSipAmount ?? 0
  })

  const openPlanBottomSheet = () => {
    openSipPlanRecommendationBottomSheet({
      isin,
      totalIdealSipAmount,
      bucketType: SipCategoryBucketKey.startRecommendations,
      recommendation,
      initiateSipCta: plansOverview?.initiateSipCta
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-start-sip`,
      label: 'Start SIP',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Start SIP', fundName, fundIsin: isin })
        onStartSip({
          isin,
          fundName,
          initiateSipCta: plansOverview?.initiateSipCta,
          amount: formattedTargetSip.value
        })
      },
      variant: 'tertiary'
    },
    {
      id: `${isin}-learn-more`,
      label: 'Learn more',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<SipFundCardTopRightLabel label="Start" targetSipAmount={formattedTargetSip} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}
